import { useMemo, useState } from 'react';
import {
  ComposedChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';

const OK = '#16a34a';
const WARN = '#d97706';
const BAD = '#dc2626';

function levelColor(absPct) {
  if (absPct < 5) return OK;
  if (absPct < 15) return WARN;
  return BAD;
}

function formatDate(ts) {
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function ErrorTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="custom-tooltip">
      <div className="tt-time">{formatDate(p.x)}</div>
      <div className="tt-value" style={{ fontWeight: 600, color: levelColor(Math.abs(p.errPct)) }}>
        Error: {p.errPct >= 0 ? '+' : ''}{p.errPct.toFixed(1)}%
      </div>
      <div className="tt-value">Detected: {p.detBbls.toFixed(1)} bbl</div>
      <div className="tt-value">Ticket #{p.ticket}: {p.ticketBbls.toFixed(1)} bbl</div>
      <div style={{ color: '#898781', marginTop: 3, fontSize: 11 }}>click point for details</div>
    </div>
  );
}

/**
 * ErrorPlot — for the selected well, plots the % error (detected vs ticket bbl)
 * of every matched haul over time. Clicking a point reveals the full
 * reconciliation: the fitted line shift (inches), its bbl equivalent, and the
 * reported ticket volume for that day.
 */
function ErrorPlot({ hauls, bblPerIn, numTanks, wellName, fullLoaded = true, onReviewHaul }) {
  const [selected, setSelected] = useState(null);

  // Only hauls that were matched to a ticket can be error-checked.
  const points = useMemo(() => {
    if (bblPerIn <= 0) return [];
    return hauls
      .filter(h => h.ticket != null && h.ticket_bbls != null)
      .map((h, i) => {
        const detBbls = h.haul_volume_ft * bblPerIn;
        const errBbl = detBbls - h.ticket_bbls;
        const errPct = h.ticket_bbls ? (100 * errBbl) / h.ticket_bbls : 0;
        return {
          i,
          x: new Date(h.drop_start).getTime(),
          errPct,
          errBbl,
          detBbls,
          ticketBbls: h.ticket_bbls,
          ticket: h.ticket,
          haul: h,
        };
      })
      .sort((a, b) => a.x - b.x);
  }, [hauls, bblPerIn]);

  const stats = useMemo(() => {
    if (!points.length) return null;
    const abs = points.map(p => Math.abs(p.errPct));
    const signed = points.map(p => p.errPct);
    const mean = arr => arr.reduce((s, v) => s + v, 0) / arr.length;
    const sorted = [...abs].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    return {
      n: points.length,
      avgAbs: mean(abs),
      avgSigned: mean(signed),
      median,
      within5: abs.filter(v => v < 5).length,
      within15: abs.filter(v => v >= 5 && v < 15).length,
      over15: abs.filter(v => v >= 15).length,
    };
  }, [points]);

  if (bblPerIn <= 0) {
    return <div className="no-data">Set a tank diameter to compute barrel errors.</div>;
  }
  if (!points.length) {
    if (!fullLoaded) {
      return (
        <div className="no-data">
          <div className="loading-spinner small" style={{ margin: '0 auto 10px' }} />
          Loading full history to match haul tickets…
        </div>
      );
    }
    return (
      <div className="no-data">
        No haul tickets matched for {wellName} in this range.<br />
        <span style={{ fontSize: 12 }}>
          (This well may not be in the ticket log, or tickets fall outside the selected dates.)
        </span>
      </div>
    );
  }

  const sel = selected != null ? points.find(p => p.i === selected) : null;

  return (
    <div className="error-plot-wrap">
      {/* Summary tiles */}
      <div className="err-stats-row">
        <div className="err-stat">
          <div className="err-stat-label">Avg abs error</div>
          <div className="err-stat-value" style={{ color: levelColor(stats.avgAbs) }}>
            {stats.avgAbs.toFixed(1)}%
          </div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Avg signed error</div>
          <div className="err-stat-value">
            {stats.avgSigned >= 0 ? '+' : ''}{stats.avgSigned.toFixed(1)}%
          </div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Median abs error</div>
          <div className="err-stat-value">{stats.median.toFixed(1)}%</div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Matched hauls</div>
          <div className="err-stat-value">{stats.n}</div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Within tolerance</div>
          <div className="err-stat-value err-breakdown">
            <span style={{ color: OK }}>{stats.within5}</span>
            {' / '}
            <span style={{ color: WARN }}>{stats.within15}</span>
            {' / '}
            <span style={{ color: BAD }}>{stats.over15}</span>
          </div>
        </div>
      </div>

      <div className="error-plot-card">
        <h2>Daily Haul Error — {wellName}</h2>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid stroke="#e1e0d9" strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="x"
              domain={['dataMin', 'dataMax']}
              scale="time"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: '#898781' }}
            />
            <YAxis
              dataKey="errPct"
              tickFormatter={v => `${v}%`}
              tick={{ fontSize: 11, fill: '#898781' }}
            />
            <Tooltip content={<ErrorTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            <ReferenceLine y={0} stroke="#898781" />
            <ReferenceLine y={5} stroke={WARN} strokeDasharray="4 4" strokeOpacity={0.5} />
            <ReferenceLine y={-5} stroke={WARN} strokeDasharray="4 4" strokeOpacity={0.5} />
            <ReferenceLine y={15} stroke={BAD} strokeDasharray="4 4" strokeOpacity={0.5} />
            <ReferenceLine y={-15} stroke={BAD} strokeDasharray="4 4" strokeOpacity={0.5} />
            <Scatter
              data={points}
              onClick={(pt) => {
                const idx = pt?.payload?.i ?? pt?.i;
                setSelected(idx === selected ? null : idx);
              }}
              cursor="pointer"
              shape={(props) => {
                const { cx, cy, payload } = props;
                if (cx == null || cy == null) return null;
                const isSel = payload.i === selected;
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={isSel ? 8 : 5}
                    fill={levelColor(Math.abs(payload.errPct))}
                    stroke={isSel ? '#1a1a1a' : '#fff'}
                    strokeWidth={isSel ? 2 : 1}
                  />
                );
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {sel && <ErrorDetail p={sel} bblPerIn={bblPerIn} numTanks={numTanks} onClose={() => setSelected(null)} onReviewHaul={onReviewHaul} />}
    </div>
  );
}

/** Detail panel: full reconciliation calc for one selected haul. */
function ErrorDetail({ p, bblPerIn, numTanks, onClose, onReviewHaul }) {
  const h = p.haul;
  const shiftIn = h.haul_volume_ft;                 // fitted line shift (inches of level)
  const perTank = shiftIn * (bblPerIn / numTanks);  // bbl per single tank
  const detBbls = shiftIn * bblPerIn;               // total detected bbl (all tanks)
  const absPct = Math.abs(p.errPct);
  const color = levelColor(absPct);

  return (
    <div className="error-detail-card">
      <div className="err-detail-head">
        <div>
          <span className="err-detail-title">Haul on {formatDateTime(h.drop_start)}</span>
          <span className="err-detail-ticket">Ticket #{h.ticket}</span>
        </div>
        <div className="err-detail-actions">
          {onReviewHaul && (
            <button
              className="err-review-btn"
              onClick={() => onReviewHaul(h)}
              title="Open this haul on the tank-level chart"
            >
              Review on chart →
            </button>
          )}
          <button className="err-detail-close" onClick={onClose}>×</button>
        </div>
      </div>

      <div className="err-detail-grid">
        {/* Step 1 — line shift */}
        <div className="err-step">
          <div className="err-step-num">1</div>
          <div className="err-step-body">
            <div className="err-step-label">Fitted level shift (before → after the drop)</div>
            <div className="err-step-calc">
              {h.level_before_ft.toFixed(1)} in − {h.level_after_ft.toFixed(1)} in
              {' = '}<strong>{shiftIn.toFixed(1)} in</strong>
            </div>
            <div className="err-step-note">
              pre-fit R² {isFinite(h.pre_r_squared) ? h.pre_r_squared.toFixed(2) : '—'}
              {' · '}post-fit R² {isFinite(h.post_r_squared) ? h.post_r_squared.toFixed(2) : '—'}
            </div>
          </div>
        </div>

        {/* Step 2 — bbl equivalent */}
        <div className="err-step">
          <div className="err-step-num">2</div>
          <div className="err-step-body">
            <div className="err-step-label">Convert to barrels</div>
            <div className="err-step-calc">
              {shiftIn.toFixed(1)} in × {(bblPerIn / numTanks).toFixed(2)} bbl/in/tank × {numTanks} tank{numTanks > 1 ? 's' : ''}
            </div>
            <div className="err-step-calc">
              = {perTank.toFixed(1)} bbl/tank × {numTanks} = <strong>{detBbls.toFixed(1)} bbl detected</strong>
            </div>
          </div>
        </div>

        {/* Step 3 — ticket */}
        <div className="err-step">
          <div className="err-step-num">3</div>
          <div className="err-step-body">
            <div className="err-step-label">Reported on ticket that day</div>
            <div className="err-step-calc">
              Ticket #{h.ticket}: <strong>{p.ticketBbls.toFixed(1)} bbl</strong>
            </div>
            {h.ticket_dt && (
              <div className="err-step-note">ticket dated {formatDate(new Date(h.ticket_dt).getTime())}</div>
            )}
          </div>
        </div>

        {/* Step 4 — error */}
        <div className="err-step err-step-result" style={{ borderColor: color }}>
          <div className="err-step-num" style={{ background: color }}>=</div>
          <div className="err-step-body">
            <div className="err-step-label">Discrepancy (detected − ticket)</div>
            <div className="err-step-calc" style={{ color, fontSize: 16, fontWeight: 700 }}>
              {p.errBbl >= 0 ? '+' : ''}{p.errBbl.toFixed(1)} bbl
              {'  ('}{p.errPct >= 0 ? '+' : ''}{p.errPct.toFixed(1)}%{')'}
            </div>
            <div className="err-step-note">
              {absPct < 5
                ? 'Within tolerance — detected volume matches the ticket.'
                : absPct < 15
                  ? 'Moderate discrepancy — worth a look.'
                  : 'Large discrepancy — likely a ticketing error or wrong tank count.'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ErrorPlot;
